import React, { useContext, createContext, useState, useCallback, useMemo } from 'react'
import { useApolloClient } from '@apollo/client'
import dayjs from 'dayjs'

import { DAILY_BILL, DAILY_REPORT, DAILY_ITEM_SALES_REPORT, MONTHLY_REPORT, MONTHLY_ITEM_SALES_REPORT, REGENERATE_REPORT, REGENERATE_SERVICE_ITEM_REPORT } from 'query'

const ReportContext = createContext(null)

const ReportProvider = ({ children }) => {
  const apolloClient = useApolloClient()
  const [reportLoading, setReportLoading] = useState('');

  const getDailyReport = useCallback(async(date) => {
    setReportLoading('show-daily-summary');
    try {
      let result = await apolloClient.query({
        query: DAILY_REPORT,
        variables: { date },
        fetchPolicy: 'network-only',
      })
      return result
    }catch(err){
      console.log("ReportProvider getDailyReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const getDailyFullReport = useCallback(async(date) => {
    setReportLoading('show-daily-full-report');
    try {
      let result = await apolloClient.query({
        query: DAILY_BILL,
        variables: { date }
      })
      return result
    }catch(err){
      console.log("ReportProvider getDailyFullReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const getDailyItemSalesReport = useCallback(async(date) => {
    setReportLoading('show-daily-item-sales-report');
    try {
      let result = await apolloClient.query({
        query: DAILY_ITEM_SALES_REPORT,
        variables: { date },
        fetchPolicy: 'network-only',
      })
      return result
    }catch(err){
      console.log("ReportProvider getDailyItemSalesReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const getMonthlyReport = useCallback(async(month = dayjs().subtract(1, "months").format('MMMM'), year = dayjs().format('YYYY')) => {
    setReportLoading('show-monthly-report');
    try {
      let result = await apolloClient.query({
        query: MONTHLY_REPORT,
        variables: { month, year },
        fetchPolicy: 'network-only',
      })
      return result
    }catch(err){
      console.log("ReportProvider getMonthlyReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const getMonthlyItemSalesReport = useCallback(async(month = dayjs().subtract(1, "months").format('MMMM'), year = dayjs().format('YYYY')) => {
    setReportLoading('show-monthly-item-sales-report');
    try {
      let result = await apolloClient.query({
        query: MONTHLY_ITEM_SALES_REPORT,
        variables: { month, year },
        fetchPolicy: 'network-only',
      })
      return result
    }catch(err){
      console.log("ReportProvider getMonthlyItemSalesReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const regenerateReport = useCallback(async({ month, year }) => {
    setReportLoading('regenerate-report');
    try {
      let result = await apolloClient.mutate({
        mutation: REGENERATE_REPORT,
        variables: {
          input: { month, year }
        }
      });

      return result.data.refreshReport || null;
    }catch(err){
      console.log("ReportProvider regenerateReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const regenerateServiceItemReport = useCallback(async({ month, year }) => {
    setReportLoading('regenerate-service-item-report');
    try {
      let result = await apolloClient.mutate({
        mutation: REGENERATE_SERVICE_ITEM_REPORT,
        variables: {
          input: { month, year }
        }
      });

      return result.data.regenerateServiceItemReport || null;
    }catch(err){
      console.log("ReportProvider regenerateServiceItemReport", err)
    } finally {
      setReportLoading('');
    }
  }, [apolloClient])

  const value = useMemo(() => ({
    reportLoading,
    getDailyReport,
    getDailyFullReport,
    getDailyItemSalesReport,
    getMonthlyReport,
    getMonthlyItemSalesReport,
    regenerateReport,
    regenerateServiceItemReport,
  }), [
    reportLoading,
    getDailyReport,
    getDailyFullReport,
    getDailyItemSalesReport,
    getMonthlyReport,
    getMonthlyItemSalesReport,
    regenerateReport,
    regenerateServiceItemReport,
  ])

  return (
    <ReportContext.Provider
      value={value}
    >
      {children}
    </ReportContext.Provider>
  )
}

const useReport = () => {
  const clientUser = useContext(ReportContext)
  if (clientUser == null) {
    throw new Error('ReportContext() called outside of a AuthProvider?')
  }
  return clientUser
}

export { ReportProvider, useReport }
